import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { usePlayerAuth } from "../context/PlayerAuthContext";
import { useProgress } from "../context/ProgressContext";
import { CHAPTERS } from "../data/chapters";
import { ACADEMY_LOGO_H } from "../data/assets";
import { BookOpen, NotebookPen, Upload, Trash2, LogOut, CheckCircle2, Clock, AlertCircle, Video, Sparkles, Trophy, Share2, Flame, X, Settings, Lock, Shirt, Crown, CreditCard, ArrowRight, Bell, LifeBuoy, BadgeCheck, Clapperboard } from "lucide-react";
import Leaderboard from "../components/Leaderboard";
import BadgeCelebration from "../components/BadgeCelebration";
import PlayerOfWeek from "../components/PlayerOfWeek";
import { shareBadgeImage, shareWeeklyImage } from "../lib/shareBadge";
import { AcademyWaitlistLock } from "../components/AcademyWaitlistLock";
import WhatsNewModal, { WHATS_NEW_VERSION } from "../components/WhatsNewModal";
import WeeklySession from "../components/WeeklySession";
import MondayNudge from "../components/MondayNudge";
import EliteGreeting from "../components/hub/EliteGreeting";
import PlayerOfWeekBanner from "../components/hub/PlayerOfWeekBanner";
import AcademyFeatures from "../components/hub/AcademyFeatures";
import ReferralCard from "../components/ReferralCard";
import ReadinessModule from "../components/readiness/ReadinessModule";
import TrainingPlans from "../components/TrainingPlans";
import UnlockModal from "../components/hub/UnlockModal";
import PerformanceRing from "../components/elite/PerformanceRing";
import GoldSparkButton from "../components/elite/GoldSparkButton";
import ProfileSwitcher from "../components/hub/ProfileSwitcher";
import HelpCenter from "../components/HelpCenter";
import { MOVES } from "../data/moves";
import { EngineSummary, EngineReportModal } from "../components/EngineReport";
import { eliteConfetti } from "../lib/eliteConfetti";
import LockerRoomFeed from "../components/hub/LockerRoomFeed";
import TechnicalTicker from "../components/elite/TechnicalTicker";
import CertificateCard from "../components/hub/CertificateCard";
import SessionHistoryCard from "../components/hub/SessionHistoryCard";
import MyHighlights from "../components/hub/MyHighlights";
import TeamHighlights, { PushAlertsCard } from "../components/hub/TeamHighlights";
import CertificateReveal from "../components/hub/CertificateReveal";
import TechnicalPulse from "../components/hub/TechnicalPulse";
import ORankMedallion from "../components/hub/ORankMedallion";
import EliteDossiersCard from "../components/hub/EliteDossiersCard";
import { SharePromptBanner } from "../components/recaps/LatestRecapPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  pending: { label: "Awaiting Review", color: "#EAB308", icon: Clock },
  approved: { label: "Approved", color: "#22C55E", icon: CheckCircle2 },
  needs_work: { label: "Needs Work", color: "#C41E24", icon: AlertCircle },
};

export default function Hub() {
  const navigate = useNavigate();
  const { player, token, logout, refresh, openBillingPortal } = usePlayerAuth();
  const [billingBusy, setBillingBusy] = useState(false);
  const manageBilling = async () => {
    setBillingBusy(true);
    try { await openBillingPortal(); }
    catch (err) { setBillingBusy(false); toast.error(err?.response?.data?.detail || "Couldn't open billing right now. Please try again shortly."); }
  };
  const { completed, entries } = useProgress();
  const [proofs, setProofs] = useState([]);
  const tier = player?.tier || "ebook";
  const isElite = tier === "elite";
  const isTrial = !!player?.is_trial;
  const trialDaysLeft = (() => {
    if (!isTrial || !player?.trial_expires_at) return null;
    const ms = new Date(player.trial_expires_at).getTime() - Date.now();
    return ms <= 0 ? 0 : Math.max(1, Math.ceil(ms / 86400000));
  })();
  const isPaid = tier === "academy" || tier === "elite";
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [unlockCtx, setUnlockCtx] = useState({});
  const openUnlock = (ctx = {}) => { setUnlockCtx(ctx); setUnlockOpen(true); };
  const [newFeedback, setNewFeedback] = useState(new Set());
  const [moveLabel, setMoveLabel] = useState("");
  const [moveId, setMoveId] = useState("");
  const [reportProof, setReportProof] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [celebrateBadge, setCelebrateBadge] = useState(null);
  const [playerBadge, setPlayerBadge] = useState(0);
  const [weekStats, setWeekStats] = useState(null);
  const [orank, setOrank] = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const fileRef = useRef();
  const proofsRef = useRef();
  const [hubTab, setHubTab] = useState(() => (new URLSearchParams(window.location.search).get("tab") === "highlights" ? "highlights" : "proofs"));
  const [highlights, setHighlights] = useState(null);
  const loadHighlights = () => axios.get(`${API}/highlights/me`, { headers: { Authorization: `Bearer ${token}` } }).then(({ data }) => setHighlights(data)).catch(() => setHighlights({ highlights: [], unseen: 0, share_consent: false }));
  useEffect(() => { if (token) loadHighlights(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps
  const highlightToastRef = useRef(false);
  useEffect(() => {
    if (highlights?.unseen > 0 && !highlightToastRef.current) {
      highlightToastRef.current = true;
      toast(`Coach Barker posted ${highlights.unseen > 1 ? `${highlights.unseen} new highlights` : "a new highlight"} of you`, { description: "Open My Highlights to watch and share.", icon: <Clapperboard size={16} className="text-[#D4AF37]" />, duration: 8000, action: { label: "Watch", onClick: () => { setHubTab("highlights"); proofsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }); } } });
    }
  }, [highlights]);
  const onHighlightSeen = (id) => setHighlights((h) => h ? { ...h, unseen: Math.max(0, h.unseen - 1), highlights: h.highlights.map((x) => (x.id === id ? { ...x, seen: true } : x)) } : h);
  const unseenHighlights = highlights?.unseen || 0;
  const bellCount = newFeedback.size + unseenHighlights;

  const pct = Math.round((completed.length / CHAPTERS.length) * 100);

  // Elite premium surface helpers (only affect Tier-3 view; Academy/eBook keep red/black).
  const staticCard = isElite ? "glass-card rounded-xl border border-[#D4AF37]/15" : "bg-[#141414] border border-white/10 rounded-lg";
  const interactiveCard = isElite ? "glass-gold rounded-xl" : "bg-[#141414] border border-white/10 hover:border-[#C41E24]/50 rounded-lg";
  const accent = isElite ? "#D4AF37" : "#C41E24";

  // Streak-at-risk banner: active streak (ends yesterday) but no session logged today.
  const todayStr = new Date().toISOString().slice(0, 10);
  const streakAtRisk = useMemo(() => {
    const days = new Set((entries || []).map((e) => e.date).filter(Boolean));
    if (days.has(todayStr)) return 0; // already trained today — safe
    let count = 0;
    const d = new Date();
    d.setDate(d.getDate() - 1); // start counting from yesterday
    while (days.has(d.toISOString().slice(0, 10))) { count += 1; d.setDate(d.getDate() - 1); }
    return count;
  }, [entries, todayStr]);
  const [streakDismissed, setStreakDismissed] = useState(() => localStorage.getItem("nexto_streak_warn_dismissed") === todayStr);
  const dismissStreak = () => { localStorage.setItem("nexto_streak_warn_dismissed", todayStr); setStreakDismissed(true); };

  // One-time "What's New" announcement (per player, per version).
  const whatsNewKey = player?.id ? `nexto_whatsnew_${WHATS_NEW_VERSION}_${player.id}` : null;
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  useEffect(() => {
    if (whatsNewKey && !localStorage.getItem(whatsNewKey)) {
      const t = setTimeout(() => setShowWhatsNew(true), 3500);
      return () => clearTimeout(t);
    }
  }, [whatsNewKey]);
  const dismissWhatsNew = () => { if (whatsNewKey) localStorage.setItem(whatsNewKey, "1"); setShowWhatsNew(false); };

  // Profile nudge: player hasn't set Club and/or School yet.
  const missingRivalry = !!player && (!player.club_name || !player.school_name);
  const [nudgeDismissed, setNudgeDismissed] = useState(false);

  // Celebrate the moment a player crosses a new streak-badge milestone.
  useEffect(() => {
    if (!token || !player?.id) return;
    axios.get(`${API}/leaderboard`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => {
        const badge = data?.you?.badge || 0;
        setPlayerBadge(badge);
        if (data?.you) setWeekStats(data.you);
        if (!badge) return;
        const key = `nexto_badge_${player.id}`;
        const seen = parseInt(localStorage.getItem(key) || "0", 10);
        if (badge > seen) {
          setCelebrateBadge(badge);
          localStorage.setItem(key, String(badge));
        }
      }).catch(() => {});
  }, [token, player]);

  const loadProofs = () => axios.get(`${API}/proofs`, { headers: { Authorization: `Bearer ${token}` } })
    .then(({ data }) => {
      setProofs(data);
      // Feedback stays flagged as "new" until the player opens the bell / views it.
      const unseen = data.filter((p) => !p.feedback_seen && (p.reaction_emoji || p.voice_path)).map((p) => p.id);
      setNewFeedback(new Set(unseen));
    }).catch(() => {});
  useEffect(() => { if (token) loadProofs(); /* eslint-disable-next-line */ }, [token]);

  // Poll while any clip is still being scanned by the Engine, so the report appears live.
  useEffect(() => {
    if (!token) return;
    const scanning = proofs.some((p) => p.engine_status === "scanning" || (!p.engine && p.engine_status !== "failed" && p.engine_status !== "done"));
    if (!scanning) return;
    const t = setInterval(loadProofs, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [token, proofs]);

  // Elite Mastered: gold confetti the first time a scan hits 9-10 on all three axes.
  useEffect(() => {
    const elite = proofs.filter((p) => p.engine?.elite);
    if (elite.length === 0) return;
    let done;
    try { done = new Set(JSON.parse(localStorage.getItem("nexto_elite_celebrated") || "[]")); }
    catch { done = new Set(); }
    const fresh = elite.filter((p) => !done.has(p.id));
    if (fresh.length === 0) return;
    eliteConfetti();
    toast.success("ELITE MASTERED — 9+ on every axis. Outstanding.");
    fresh.forEach((p) => done.add(p.id));
    localStorage.setItem("nexto_elite_celebrated", JSON.stringify([...done]));
  }, [proofs]);

  // Notification bell: jump to the video proofs, then mark all new feedback as seen.
  const openFeedback = () => {
    const ids = [...newFeedback];
    if (ids.length === 0 && unseenHighlights > 0) setHubTab("highlights"); else setHubTab("proofs");
    proofsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    ids.forEach((id) => axios.post(`${API}/proofs/${id}/seen`, {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {}));
    setNewFeedback(new Set());
  };

  const onFilePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) { setSelectedFile(null); return; }
    if (file.size > 60 * 1024 * 1024) {
      toast.error("Video too large (max 60MB).");
      e.target.value = "";
      setSelectedFile(null);
      return;
    }
    setSelectedFile(file);
  };

  const upload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", selectedFile);
    fd.append("move_label", moveLabel || "My move");
    fd.append("move_id", moveId || "");
    try {
      await axios.post(`${API}/proofs`, fd, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded! The Technical Performance Engine is scanning your clip…");
      setMoveLabel("");
      setMoveId("");
      setSelectedFile(null);
      loadProofs();
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async (id) => {
    await axios.delete(`${API}/proofs/${id}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    loadProofs();
  };

  return (
    <div className={`min-h-screen text-white relative ${isElite ? "elite-carbon-bg elite-hub" : "bg-[#111]"}`} data-testid={isElite ? "hub-elite" : "hub-standard"}>
      {isElite && (
        <>
          <div aria-hidden className="pointer-events-none fixed inset-0 z-0 elite-mesh-overlay" />
          <div aria-hidden className="pointer-events-none fixed inset-0 z-0" style={{ background: "radial-gradient(900px 500px at 80% -10%, rgba(212,175,55,0.12), transparent 60%), radial-gradient(700px 400px at 0% 10%, rgba(212,175,55,0.06), transparent 55%)" }} />
        </>
      )}
      <div className="relative z-10 with-bottom-nav">
      {celebrateBadge && <BadgeCelebration badge={celebrateBadge} onClose={() => setCelebrateBadge(null)} />}
      <WhatsNewModal open={showWhatsNew} onClose={dismissWhatsNew} onUpdateProfile={() => { dismissWhatsNew(); navigate("/profile"); }} />
      <MondayNudge suppress={showWhatsNew} />
      <CertificateReveal token={token} playerId={player?.id} suppress={showWhatsNew} />
      <UnlockModal open={unlockOpen} player={player} email={player?.email} tier={tier} eliteOnly={unlockCtx.eliteOnly} featureTitle={unlockCtx.featureTitle} onClose={() => setUnlockOpen(false)} />
      {helpOpen && <HelpCenter onClose={() => setHelpOpen(false)} />}
      <header className={`sticky top-0 z-30 backdrop-blur-xl px-4 sm:px-6 pb-3 sm:pb-4 header-safe flex items-center justify-between gap-2 ${isElite ? "bg-[#0a0a0a]/80 border-b border-[#D4AF37]/25" : "bg-[#111]/85 border-b border-white/10"}`}>
        <button onClick={() => navigate("/")} className="flex items-center gap-2 sm:gap-3 min-w-0">
          <img src={ACADEMY_LOGO_H} alt="The Next O Technical Academy" className="h-9 sm:h-12 w-auto object-contain shrink-0" />
          <div className="text-left min-w-0 hidden md:block">
            <div className={`text-[10px] uppercase tracking-widest font-bold flex items-center gap-1.5 ${isElite ? "text-[#D4AF37]" : "text-[#C41E24]"}`}>
              {isElite && <Crown size={11} />} {isElite ? "Elite Hub" : "Player Hub"}
            </div>
            <div className="font-headings text-base uppercase tracking-wide truncate max-w-[120px] sm:max-w-none">{player?.display_name || player?.name || "Player"}</div>
          </div>
        </button>
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          <ProfileSwitcher />
          <button data-testid="hub-feedback-bell" onClick={openFeedback} aria-label="Coach feedback notifications"
            className="relative flex items-center justify-center w-11 h-11 rounded-sm border border-white/15 hover:border-white/40 text-white/60 hover:text-white transition-colors">
            <Bell size={16} className={bellCount > 0 ? "text-[#C41E24]" : ""} />
            {bellCount > 0 && (
              <span data-testid="hub-feedback-count" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-[#C41E24] text-white text-[10px] font-bold border-2 border-[#0d0d0d] animate-pulse">{bellCount}</span>
            )}
          </button>
          {isPaid && (
            <span data-testid="tier-badge" className={`hidden sm:flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-2 rounded-sm border ${isElite ? "text-[#D4AF37] border-[#D4AF37]/50 bg-[#D4AF37]/10" : "text-[#C41E24] border-[#C41E24]/50 bg-[#C41E24]/10"}`}>
              {isElite ? <Crown size={12} /> : <Sparkles size={12} />} {isElite ? (isTrial ? "Elite Trial" : "Elite") : "Technical Track"}
            </span>
          )}
          {player?.has_billing && (
            <button data-testid="hub-manage-billing" onClick={manageBilling} disabled={billingBusy}
              className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-white/60 hover:text-white border border-white/15 hover:border-white/40 px-3 py-2 rounded-sm transition-colors disabled:opacity-60">
              <CreditCard size={14} /> <span className="hidden sm:inline">{billingBusy ? "Opening…" : "Manage Enrollment"}</span>
            </button>
          )}
          <button data-testid="hub-help-btn" onClick={() => setHelpOpen(true)} aria-label="Help center"
            className="flex items-center justify-center w-11 h-11 rounded-sm border border-white/15 hover:border-white/40 text-white/60 hover:text-white transition-colors" title="Help">
            <LifeBuoy size={16} />
          </button>
          <button data-testid="hub-settings" onClick={() => navigate("/profile")} className="hidden sm:flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-white/60 hover:text-white border border-white/15 hover:border-white/40 px-3 py-2 rounded-sm transition-colors"><Settings size={14} /> <span className="hidden sm:inline">Profile</span></button>
          <button data-testid="hub-logout" onClick={() => { logout(); navigate("/"); }} className="hidden sm:flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-white/60 hover:text-white border border-white/15 hover:border-white/40 px-3 py-2 rounded-sm transition-colors"><LogOut size={14} /> <span className="hidden sm:inline">Log Out</span></button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-10">
        <SharePromptBanner token={token} />
        <PlayerOfWeekBanner playerId={player?.id} />
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className={`font-cover text-5xl sm:text-6xl uppercase tracking-tight ${isElite ? "text-transparent bg-clip-text bg-gradient-to-r from-[#F5E7B8] via-[#D4AF37] to-[#B8912E]" : ""}`}>Welcome back{(player?.display_name || player?.name) ? `, ${(player.display_name || player.name).split(" ")[0]}` : ""}.</h1>
          <div data-testid="hub-mobile-identity" className="md:hidden flex flex-wrap items-center gap-2 mt-3">
            <span className="font-headings text-base uppercase tracking-wide text-white/90">{player?.display_name || player?.name || "Player"}</span>
            <span data-testid="hub-mobile-tier" className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm border ${isElite ? "text-[#D4AF37] border-[#D4AF37]/50 bg-[#D4AF37]/10" : "text-[#C41E24] border-[#C41E24]/50 bg-[#C41E24]/10"}`}>
              {isElite ? <Crown size={11} /> : <Sparkles size={11} />} {isElite ? (isTrial ? "Elite Trial" : "Elite") : isPaid ? "Technical Track" : "Player Hub"}
            </span>
          </div>
          <p className="text-white/60 mt-2">{isElite ? "Your elite edge starts here." : "Master the ball. Master the game."}</p>
          {isTrial && trialDaysLeft !== null && (
            <div data-testid="trial-banner" className="mt-4 flex flex-wrap items-center gap-3 bg-[#D4AF37]/10 border border-[#D4AF37]/40 rounded-lg px-4 py-2.5">
              <Crown size={16} className="text-[#D4AF37] shrink-0" />
              <span className="text-sm text-white/85">
                {trialDaysLeft === 0
                  ? <>Your <b className="text-[#D4AF37]">Elite trial</b> has ended — enroll to keep your Hub, points & videos.</>
                  : <><b className="text-[#D4AF37]">Elite Trial</b> — <b className="text-white">{trialDaysLeft} day{trialDaysLeft !== 1 ? "s" : ""} left</b> of full Elite access.</>}
              </span>
              <button data-testid="trial-upgrade-btn" onClick={() => navigate("/subscribe?tier=elite")}
                className="ml-auto inline-flex items-center gap-1.5 bg-gradient-to-r from-[#E7C64B] to-[#C39A2B] hover:brightness-110 text-black text-xs font-bold uppercase tracking-wider px-3.5 py-2 rounded-sm transition-all">
                Enroll <ArrowRight size={13} />
              </button>
            </div>
          )}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,340px)_1fr] gap-4 mb-8 items-stretch">
          <ORankMedallion token={token} player={player} entries={entries} isElite={isElite} onData={setOrank} />
          <TechnicalPulse entries={entries} isElite={isElite} playerId={player?.id} />
        </div>

        <EliteGreeting player={player} isElite={isElite} onLockedClick={() => openUnlock({ eliteOnly: true, featureTitle: "Personal Coach Videos" })} />

        {/* Streak-at-risk banner */}
        {streakAtRisk > 0 && !streakDismissed && (
          <motion.div
            data-testid="streak-warning-banner"
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
            className="mb-8 flex items-center gap-4 rounded-lg border border-[#C41E24]/60 bg-gradient-to-r from-[#C41E24]/20 to-[#141414] px-5 py-4">
            <div className="shrink-0 w-11 h-11 rounded-full bg-[#C41E24]/20 flex items-center justify-center">
              <Flame size={22} className="text-[#C41E24] animate-pulse" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-subheadings font-bold uppercase tracking-wide text-sm">You'll lose your {streakAtRisk}-day streak if you skip today</div>
              <div className="text-xs text-white/55 mt-0.5">Log a quick session before the day ends to keep it alive.</div>
            </div>
            <button data-testid="streak-warning-log-btn" onClick={() => navigate(`/read/journal`)}
              className="shrink-0 bg-[#C41E24] hover:bg-[#A0161D] text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-sm transition-colors">
              Log Today
            </button>
            <button data-testid="streak-warning-dismiss" onClick={dismissStreak} aria-label="Dismiss"
              className="shrink-0 text-white/40 hover:text-white transition-colors"><X size={18} /></button>
          </motion.div>
        )}

        {/* Profile nudge: missing Club/School */}
        {missingRivalry && !nudgeDismissed && (
          <motion.div
            data-testid="rivalry-nudge-banner"
            initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
            className="mb-8 flex items-center gap-4 rounded-lg border border-[#F5C518]/40 bg-gradient-to-r from-[#F5C518]/12 to-[#141414] px-5 py-4">
            <div className="shrink-0 w-11 h-11 rounded-full bg-[#F5C518]/15 flex items-center justify-center">
              <Shirt size={22} className="text-[#F5C518]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-subheadings font-bold uppercase tracking-wide text-sm">Add your Club &amp; School to join the race</div>
              <div className="text-xs text-white/55 mt-0.5">Power up the Top Clubs &amp; Top Schools boards — and get in the running for the Sept 30 jersey.</div>
            </div>
            <button data-testid="rivalry-nudge-btn" onClick={() => navigate("/profile")}
              className="shrink-0 bg-[#C41E24] hover:bg-[#A0161D] text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-sm transition-colors">
              Update Profile
            </button>
            <button data-testid="rivalry-nudge-dismiss" onClick={() => setNudgeDismissed(true)} aria-label="Dismiss"
              className="shrink-0 text-white/40 hover:text-white transition-colors"><X size={18} /></button>
          </motion.div>
        )}

        {/* Progress + quick actions */}
        <WeeklySession isElite={isElite} />
        <AcademyFeatures isPaid={isPaid} isElite={isElite} onLockedClick={(f) => openUnlock({ eliteOnly: f.eliteOnly, featureTitle: f.title })} onOpenRoadmap={() => navigate("/roadmap")} />
        <div className="mb-10"><ReferralCard token={token} /></div>
        <CertificateCard token={token} />
        <SessionHistoryCard token={token} />
        <EliteDossiersCard token={token} isElite={isElite} onLocked={() => openUnlock({ eliteOnly: true, featureTitle: "Monthly Scouting Dossier" })} />
        {(isElite || !!player?.school_id) && <ReadinessModule token={token} />}
        {(isElite || !!player?.school_id) && (
          <section data-testid="hub-training-plans" className="mb-10 rounded-2xl border border-white/10 bg-[#0e0e0e]/60 p-6 sm:p-8" style={{ backdropFilter: "blur(18px)" }}>
            <div className="text-[10px] font-bold uppercase tracking-[0.28em] text-[#D4AF37] mb-1">High School Performance System</div>
            <h2 className="font-headings text-2xl sm:text-3xl uppercase tracking-wide mb-5">Your Training Plan</h2>
            <TrainingPlans accent="#C41E24" secondary="#D4AF37" token={token} />
          </section>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
          {isElite ? (
            <div data-testid="hub-progress-card" className={`${staticCard} p-5 flex items-center gap-4`}>
              <PerformanceRing percentage={pct} size={92} stroke={9} label="Chapters" testid="progress-ring" />
              <div>
                <div className="font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-[#D4AF37]/80 mb-1">Training Progress</div>
                <div className="font-mono text-2xl font-bold text-white leading-none">{completed.length}<span className="text-white/40 text-lg">/{CHAPTERS.length}</span></div>
                <div className="text-[11px] text-white/40 mt-1">chapters mastered</div>
              </div>
            </div>
          ) : (
            <div data-testid="hub-progress-card" className="bg-[#141414] border border-white/10 rounded-lg p-5">
              <div className="text-[10px] font-bold uppercase tracking-widest text-white/50 mb-2">Training Progress</div>
              <div data-testid="hub-progress-pct" className="font-cover text-4xl text-[#C41E24]">{pct}%</div>
              <div data-testid="hub-progress-bar" className="h-1.5 bg-white/10 rounded-full overflow-hidden mt-2"><div className="h-full bg-[#C41E24]" style={{ width: `${pct}%` }} /></div>
              <div className="text-[11px] text-white/40 mt-1">{completed.length} of {CHAPTERS.length} chapters</div>
            </div>
          )}
          <GoldSparkButton data-testid="hub-read-btn" sparkColor={accent} spark={isElite} contentClass="relative z-10 block w-full" onClick={() => navigate(`/read/${CHAPTERS[0].id}`)} className={`${interactiveCard} p-5 text-left transition-colors block`}>
            <span className="block">
              <BookOpen size={22} className="mb-3" style={{ color: accent }} />
              <span className="block font-subheadings font-bold uppercase tracking-wide text-sm">Read the Blueprint</span>
              <span className="block text-xs text-white/45 mt-1">12 chapters · 30+ moves</span>
            </span>
          </GoldSparkButton>
          <GoldSparkButton data-testid="hub-journal-btn" sparkColor={accent} spark={isElite} contentClass="relative z-10 block w-full" onClick={() => navigate(`/read/journal`)} className={`${interactiveCard} p-5 text-left transition-colors block`}>
            <span className="block">
              <NotebookPen size={22} className="mb-3" style={{ color: accent }} />
              <span className="block font-subheadings font-bold uppercase tracking-wide text-sm">Daily Journal</span>
              <span className="block text-xs text-white/45 mt-1">Log training & weak-foot progress</span>
            </span>
          </GoldSparkButton>
        </div>

        {/* Player of the Week */}
        <div className="mb-6"><PlayerOfWeek /></div>

        {/* Central Illinois Leaderboard */}
        <div className="flex items-center gap-2 mb-4"><Trophy size={20} style={{ color: accent }} /><h2 className="font-headings text-2xl uppercase tracking-wide">Central Illinois Leaderboard</h2></div>
        <div className={`${staticCard} p-6 mb-10`} data-testid="hub-leaderboard">
          <p className="text-sm text-white/55 mb-4">Earn points for completing chapters, uploading drill videos, logging sessions, and keeping your streak alive. Climb the ranks!</p>
          {playerBadge > 0 && (
            <button data-testid="hub-share-badge" onClick={() => shareBadgeImage(playerBadge)}
              className="mb-4 inline-flex items-center gap-2 bg-[#C41E24] hover:bg-[#A0161D] text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-sm transition-colors">
              <Share2 size={14} /> Share My {playerBadge}-Day Streak
            </button>
          )}
          {weekStats && (weekStats.points > 0) && (
            <button data-testid="hub-share-week" onClick={() => shareWeeklyImage({
                points: weekStats.points, streak: weekStats.streak, rank: weekStats.rank,
                movement: weekStats.movement, name: player?.display_name || player?.name || "" })}
              className={`mb-4 ${playerBadge > 0 ? "ml-2" : ""} inline-flex items-center gap-2 border text-xs font-bold uppercase tracking-wider px-4 py-2.5 rounded-sm transition-colors ${isElite ? "border-[#D4AF37]/50 text-[#D4AF37] hover:bg-[#D4AF37]/10" : "border-white/20 text-white/80 hover:text-white hover:border-white/40"}`}>
              <Share2 size={14} /> Share My Week
            </button>
          )}
          <Leaderboard token={token} limit={10} isElite={isElite} />
        </div>

        {/* Video proofs / My Highlights tabs */}
        <div ref={proofsRef} data-testid="hub-section-tabs" className="flex items-center gap-2 mb-4 overflow-x-auto no-scrollbar">
          <button data-testid="hub-tab-proofs" onClick={() => setHubTab("proofs")} aria-pressed={hubTab === "proofs"} className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-sm border text-xs font-bold uppercase tracking-wider transition-colors whitespace-nowrap ${hubTab === "proofs" ? "border-transparent text-black" : "border-white/15 text-white/60 hover:text-white"}`} style={hubTab === "proofs" ? { background: accent } : undefined}><Video size={14} /> Homework Proofs</button>
          <button data-testid="hub-tab-highlights" onClick={() => setHubTab("highlights")} aria-pressed={hubTab === "highlights"} className={`relative inline-flex items-center gap-2 px-4 py-2.5 rounded-sm border text-xs font-bold uppercase tracking-wider transition-colors whitespace-nowrap ${hubTab === "highlights" ? "border-transparent text-black" : "border-white/15 text-white/60 hover:text-white"}`} style={hubTab === "highlights" ? { background: "#D4AF37" } : undefined}>
            <Clapperboard size={14} /> My Highlights
            {unseenHighlights > 0 && <span data-testid="hub-tab-highlights-badge" className="ml-1 min-w-[18px] h-[18px] px-1 inline-flex items-center justify-center rounded-full bg-[#C41E24] text-white text-[10px] font-bold">{unseenHighlights}</span>}
          </button>
        </div>
        {hubTab === "highlights" && (
          <div data-testid="hub-highlights-panel" className="mb-6">
            <div className="flex items-center gap-2 mb-4"><Clapperboard size={20} style={{ color: "#D4AF37" }} /><h2 className="font-headings text-2xl uppercase tracking-wide">My Highlights</h2></div>
            <PushAlertsCard token={token} />
            <MyHighlights token={token} data={highlights} onSeen={onHighlightSeen} />
            <TeamHighlights token={token} />
          </div>
        )}
        {hubTab === "proofs" && (<>
        <div className="flex items-center gap-2 mb-4"><Video size={20} style={{ color: accent }} /><h2 className="font-headings text-2xl uppercase tracking-wide">My Video Proofs</h2></div>
        <div className={`${staticCard} p-6 mb-6`}>
          {player?.can_upload === false ? (
            <div data-testid="proof-upload-locked">
              <AcademyWaitlistLock note="You've used your free coach review. Your uploaded proof and Coach Barker's feedback are still below. Enroll for unlimited reviews." />
            </div>
          ) : (
            <>
              <p className="text-sm text-white/55 mb-4">Film a short clip (10–20s) of yourself doing a move — whole body in frame, ball visible, good lighting. {isPaid ? <span className="text-[#22C55E] font-semibold">{isElite ? "Elite" : "Technical Track"} players get unlimited coach reviews.</span> : <>Every Blueprint player gets <span className="text-white font-semibold">one free coach review</span>.</>}</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <select data-testid="proof-drill-picker" value={moveId}
                  onChange={(e) => { const id = e.target.value; setMoveId(id); const m = MOVES.find((x) => x.id === id); setMoveLabel(m ? m.name : ""); }}
                  className="flex-1 bg-[#0d0d0d] border border-white/10 rounded-md p-3 text-sm text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]">
                  <option value="">Which drill? (for accurate Engine tracking)</option>
                  <optgroup label="Foundational Touches">
                    {MOVES.filter((m) => m.tier === "touch").map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </optgroup>
                  <optgroup label="Signature Moves">
                    {MOVES.filter((m) => m.tier === "move").map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </optgroup>
                  <optgroup label="Combinations">
                    {MOVES.filter((m) => m.tier === "combo").map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </optgroup>
                </select>
                <input ref={fileRef} data-testid="proof-file" type="file" accept="video/*" onChange={onFilePick} className="hidden" />
                <GoldSparkButton data-testid="proof-choose-btn" sparkColor={accent} spark={isElite} onClick={() => fileRef.current?.click()} disabled={uploading}
                  className={`font-subheadings font-bold uppercase tracking-wider px-5 py-3 rounded-sm transition-colors disabled:opacity-60 ${isElite ? "border border-[#D4AF37]/40 hover:border-[#D4AF37] text-white/85 hover:text-white" : "border border-white/20 hover:border-[#C41E24]/60 text-white/80 hover:text-white"}`}>
                  <Video size={16} /> {selectedFile ? "Change Video" : "Choose Video"}
                </GoldSparkButton>
                <GoldSparkButton data-testid="proof-upload-btn" sparkColor={accent} spark={isElite} onClick={upload} disabled={!selectedFile || uploading}
                  className={`font-subheadings font-bold uppercase tracking-wider px-6 py-3 rounded-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${isElite ? "bg-gradient-to-r from-[#E7C64B] to-[#C39A2B] text-black shadow-[0_0_18px_rgba(212,175,55,0.35)]" : "bg-[#C41E24] hover:bg-[#A0161D] text-white"}`}>
                  <Upload size={16} /> {uploading ? "Uploading…" : "Upload Video"}
                </GoldSparkButton>
              </div>
              {selectedFile && (
                <p data-testid="proof-selected-name" className="text-xs text-white/50 mt-3 flex items-center gap-1.5 truncate">
                  <CheckCircle2 size={13} className="text-[#22C55E] flex-shrink-0" /> Selected: {selectedFile.name}
                </p>
              )}
            </>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="proof-list">
          {proofs.length === 0 && <p className="text-sm text-white/40">No uploads yet — record your first proof above.</p>}
          {proofs.map((p) => {
            const st = STATUS[p.status] || STATUS.pending;
            return (
              <div key={p.id} className={`${staticCard} overflow-hidden`}>
                <video controls preload="metadata" className="w-full aspect-video bg-black" src={`${API}/proofs/file/${p.id}?auth=${token}`} />
                <div className="p-4">
                  <div className="flex items-center justify-between mb-1 gap-2">
                    <span className="font-semibold text-sm flex items-center gap-2 min-w-0"><span className="truncate">{p.move_label || "My move"}</span>
                      {p.coach_verified && <span data-testid={`coach-verified-${p.id}`} className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm shrink-0" style={{ color: "#D4AF37", background: "rgba(212,175,55,0.14)", border: "1px solid rgba(212,175,55,0.5)" }}><BadgeCheck size={10} /> Coach-Verified</span>}
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-wider flex items-center gap-1" style={{ color: st.color }}><st.icon size={12} /> {st.label}</span>
                  </div>
                  {p.coach_note && <p className="text-xs text-white/55 mt-1 italic">Coach: {p.coach_note}</p>}
                  <EngineSummary proof={p} onOpen={() => setReportProof(p)} />
                  {(p.reaction_emoji || p.voice_path) && (
                    <div data-testid={`coach-reaction-${p.id}`} className={`mt-3 rounded-md border p-3 ${newFeedback.has(p.id) ? "border-[#C41E24] bg-[#C41E24]/10" : "border-white/10 bg-[#0d0d0d]"}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles size={13} className="text-[#C41E24]" />
                        <span className="text-[10px] font-bold uppercase tracking-widest text-white/55">Coach Reaction</span>
                        {newFeedback.has(p.id) && <span data-testid={`new-feedback-${p.id}`} className="ml-auto text-[9px] font-bold uppercase tracking-wider text-[#C41E24] bg-[#C41E24]/15 px-2 py-0.5 rounded-full animate-pulse">New feedback!</span>}
                      </div>
                      {p.reaction_emoji && <span className="text-3xl leading-none">{p.reaction_emoji}</span>}
                      {p.voice_path && <audio controls preload="none" className="w-full h-9 mt-2" src={`${API}/proofs/voice/${p.id}?auth=${token}`} />}
                    </div>
                  )}
                  <button onClick={() => remove(p.id)} data-testid={`delete-proof-${p.id}`} className="text-white/30 hover:text-[#C41E24] transition-colors mt-2 flex items-center gap-1 text-xs py-1.5"><Trash2 size={14} /> Delete</button>
                </div>
              </div>
            );
          })}
        </div>
        </>)}

        <LockerRoomFeed token={token} isElite={isElite} onLockedClick={() => openUnlock({ eliteOnly: true, featureTitle: "Private Locker Room" })} />
        {isElite && <div className="h-10" aria-hidden />}
      </div>
      </div>
      {isElite && <TechnicalTicker token={token} you={weekStats} />}
      {reportProof && (
        <EngineReportModal proof={reportProof} token={token} api={API} player={orank ? { ...player, orank } : player}
          history={proofs.filter((p) => p.engine && (reportProof.move_id ? p.move_id === reportProof.move_id : p.id === reportProof.id))}
          onClose={() => setReportProof(null)} />
      )}
    </div>
  );
}
